import React, { useState, useEffect, useRef } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { DesignSystem, getColor, getSpacing } from '../../styles/designSystem';

// ─── Animation ────────────────────────────────────────────────────────────────

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(30px); }
  to   { opacity: 1; transform: translateY(0); }
`;

// ─── Types ────────────────────────────────────────────────────────────────────

export interface CinematicHeroProps {
  /** Array of background image URLs for carousel mode */
  images?: string[];
  /** Current active index (controlled, for carousel mode) */
  activeIndex?: number;
  /** Single background image URL (legacy / non-carousel usage) */
  bgImage?: string;
  /** Viewport height, e.g. '100vh', '85vh' */
  height?: string;
  /** Mobile viewport height */
  mobileHeight?: string;
  /** CSS brightness filter (default 1.05) */
  brightness?: number;
  /** CSS contrast filter (default 1.1) */
  contrast?: number;
  /** Hero title — supports React nodes for <span> gold accents */
  title: React.ReactNode;
  /** Subtitle / description text */
  subtitle?: string;
  /** Small uppercase label above title (e.g. "DESTINATION") */
  label?: string;
  /** Tagline — stats line with dot separators, renders below subtitle */
  tagline?: React.ReactNode;
  /** Pricing pill element rendered below description */
  pricingLabel?: React.ReactNode;
  /** Ghost CTA button text */
  ctaText?: string;
  /** Ghost CTA button click handler */
  onCtaClick?: () => void;
  /** Show navigation arrows (for carousels) */
  showNavArrows?: boolean;
  /** Previous arrow click */
  onPrev?: () => void;
  /** Next arrow click */
  onNext?: () => void;
  /** Custom left/right arrow content */
  prevArrow?: React.ReactNode;
  nextArrow?: React.ReactNode;
  /** Aria labels for nav buttons */
  prevAriaLabel?: string;
  nextAriaLabel?: string;
  /** Content rendered in top-left (e.g. back button) */
  topLeftSlot?: React.ReactNode;
  /** Apply fadeInUp animation to hero content */
  animated?: boolean;
  /** Section id (e.g. "home") */
  id?: string;
  /** Children rendered after all default content inside the hero */
  children?: React.ReactNode;
}

// ─── Styled Components ────────────────────────────────────────────────────────

const HeroSection = styled.section<{
  $bgImage?: string;
  $hasCarousel?: boolean;
  $height?: string;
  $mobileHeight?: string;
  $brightness?: number;
  $contrast?: number;
}>`
  position: relative;
  height: ${p => p.$height || '100vh'};
  ${p => p.$hasCarousel ? css`
    background: #FEFCF9;
  ` : css`
    background: linear-gradient(
      135deg,
      rgba(15, 23, 42, 0.35) 0%,
      rgba(51, 65, 85, 0.25) 50%,
      rgba(30, 41, 59, 0.4) 100%
    ), url(${p.$bgImage || ''}) center/cover;
    background-attachment: scroll;
  `}
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  color: white;
  overflow: hidden;
  filter: brightness(${p => p.$brightness ?? 1.05}) contrast(${p => p.$contrast ?? 1.1});
  will-change: auto;

  /* Premium color grading overlay */
  &::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0; bottom: 0;
    background:
      linear-gradient(135deg, rgba(20, 184, 166, 0.08) 0%, rgba(6, 182, 212, 0.04) 50%, transparent 100%),
      radial-gradient(ellipse at 85% 90%, rgba(184, 175, 160, 0.06) 0%, transparent 60%),
      radial-gradient(circle at 15% 20%, rgba(255, 255, 255, 0.08) 0%, transparent 40%);
    pointer-events: none;
    z-index: 2;
  }

  /* Vignette effect for cinematic appeal */
  &::after {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0; bottom: 0;
    background: radial-gradient(
      ellipse at center,
      rgba(0, 0, 0, 0) 0%,
      rgba(0, 0, 0, 0.15) 70%,
      rgba(0, 0, 0, 0.3) 100%
    );
    pointer-events: none;
    z-index: 1;
  }

  @media (max-width: 768px) {
    height: ${p => p.$mobileHeight || '90vh'};
  }
`;

/* Carousel image layers — stacked absolutely, crossfade via opacity */
const CarouselImage = styled.img<{ $active: boolean }>`
  position: absolute;
  top: 0; left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  opacity: ${p => (p.$active ? 1 : 0)};
  transition: opacity 1s ease-in-out;
  z-index: 0;
  pointer-events: none;
`;

/* Gradient overlay on top of carousel images */
const CarouselOverlay = styled.div`
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  background: linear-gradient(
    135deg,
    rgba(15, 23, 42, 0.35) 0%,
    rgba(51, 65, 85, 0.25) 50%,
    rgba(30, 41, 59, 0.4) 100%
  );
  z-index: 0;
  pointer-events: none;
`;

const HeroContent = styled.div<{ $animated?: boolean }>`
  position: relative;
  z-index: 3;
  max-width: 900px;
  padding: 0 24px;
  ${p => p.$animated !== false ? css`animation: ${fadeInUp} 0.8s ease-out;` : ''}
`;

const HeroLabel = styled.span`
  display: inline-block;
  background: rgba(255, 255, 255, 0.12);
  backdrop-filter: blur(10px);
  color: white;
  padding: 8px 20px;
  border-radius: 30px;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 2px;
  text-transform: uppercase;
  margin-bottom: 24px;
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const HeroTitle = styled.h1`
  font-size: 3.375rem;
  font-weight: 400;
  margin-bottom: 16px;
  letter-spacing: -0.03em;
  color: white;
  text-shadow: 0 4px 30px rgba(0, 0, 0, 0.5);
  line-height: 1.1;
  font-family: ${DesignSystem.typography.fonts.display};

  span {
    color: #CAB96E;
    font-weight: 400;
  }

  @media (max-width: 768px) {
    font-size: 1.875rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.125rem;
  margin-bottom: 28px;
  font-weight: 300;
  line-height: 1.6;
  opacity: 0.95;
  color: white;
  text-shadow: 0 2px 15px rgba(0, 0, 0, 0.3);
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;

  @media (max-width: 768px) {
    font-size: 0.95rem;
  }
`;

const HeroTagline = styled.p`
  font-size: 0.85rem;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.8);
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
  margin: 0 0 52px 0;

  .separator {
    color: #CAB96E;
    margin: 0 0.5em;
    font-weight: 600;
  }

  @media (max-width: 768px) {
    font-size: 0.75rem;
    letter-spacing: 0.1em;
  }
  
  @media (max-width: 480px) {
    font-size: 0.7rem;
    letter-spacing: 0.08em;
  }
`;

// ─── Shared CTA Button Style Constants ──────────────────────────────────────
export const CTA_BUTTON_STYLES = {
  background: '#CAB96E',
  border: '1px solid #CAB96E',
  color: 'white',
  borderRadius: '50px',
  textTransform: 'uppercase' as const,
  letterSpacing: '0.2em',
  fontWeight: 500,
  fontFamily: "'DM Sans', sans-serif",
  boxShadow: '0 4px 16px rgba(101, 90, 71, 0.2)',
  cursor: 'pointer',
} as const;

const HeroCTAButton = styled.button`
  margin-top: 24px;
  padding: 14px 32px;
  font-size: 0.75rem;
  font-weight: 500;
  font-family: 'DM Sans', sans-serif;
  text-transform: uppercase;
  letter-spacing: 0.2em;
  background: rgba(202, 185, 110, 0.5);
  border-radius: 50px;
  border: 1px solid rgba(202, 185, 110, 0.35);
  color: rgba(255, 255, 255, 0.75);
  cursor: pointer;
  align-self: center;
  width: fit-content;
  box-shadow: none;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(202, 185, 110, 0.45);
    border-color: rgba(202, 185, 110, 0.5);
    transform: translateY(-2px) scale(1.02);
    box-shadow: none;
  }

  &:active {
    transform: translateY(0) scale(0.98);
  }
`;

const HeroNavButton = styled.button<{ $left?: boolean }>`
  position: absolute;
  top: 50%;
  ${p => p.$left ? 'left: 24px;' : 'right: 24px;'}
  transform: translateY(-50%);
  width: 56px;
  height: 56px;
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, 0.3);
  background: transparent;
  backdrop-filter: none;
  color: white;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: none;
  transition: all ${DesignSystem.motion.duration.fast} ${DesignSystem.motion.easing.ease};
  z-index: 100;

  &:hover {
    transform: translateY(-50%) scale(1.1);
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(255, 255, 255, 0.5);
  }

  &:active {
    transform: translateY(-50%) scale(0.95);
  }

  &:focus-visible {
    outline: 2px solid ${getColor('accent.500')};
    outline-offset: 4px;
  }

  svg {
    width: 24px;
    height: 24px;
    fill: currentColor;
    filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3));
  }

  @media (max-width: 768px) {
    width: 44px;
    height: 44px;
    ${p => p.$left ? 'left: 12px;' : 'right: 12px;'}

    svg {
      width: 20px;
      height: 20px;
    }
  }
  
  @media (max-width: 380px) {
    width: 36px;
    height: 36px;
    ${p => p.$left ? 'left: 8px;' : 'right: 8px;'}

    svg {
      width: 16px;
      height: 16px;
    }
  }
`;

// ─── Component ────────────────────────────────────────────────────────────────

const CinematicHero: React.FC<CinematicHeroProps> = ({
  images,
  activeIndex = 0,
  bgImage,
  height,
  mobileHeight,
  brightness,
  contrast,
  title,
  subtitle,
  label,
  tagline,
  pricingLabel,
  ctaText,
  onCtaClick,
  showNavArrows,
  onPrev,
  onNext,
  prevArrow,
  nextArrow,
  prevAriaLabel,
  nextAriaLabel,
  topLeftSlot,
  animated = true,
  id,
  children,
}) => {
  const isCarousel = !!(images && images.length > 0);

  // Preload all carousel images on mount
  useEffect(() => {
    if (!isCarousel || !images) return;
    images.forEach((src) => {
      const img = new Image();
      img.src = src;
    });
  }, [images, isCarousel]);

  return (
    <HeroSection
      id={id}
      $bgImage={isCarousel ? undefined : bgImage}
      $hasCarousel={isCarousel}
      $height={height}
      $mobileHeight={mobileHeight}
      $brightness={brightness}
      $contrast={contrast}
    >
      {/* Carousel crossfade layers */}
      {isCarousel && images && (
        <>
          {images.map((src, idx) => (
            <CarouselImage
              key={src}
              src={src}
              $active={idx === activeIndex}
              alt=""
              loading={idx <= 1 ? 'eager' : 'lazy'}
            />
          ))}
          <CarouselOverlay />
        </>
      )}

      {topLeftSlot}

      <HeroContent $animated={animated}>
        {label && <HeroLabel>{label}</HeroLabel>}
        <HeroTitle>{title}</HeroTitle>
        {subtitle && <HeroSubtitle>{subtitle}</HeroSubtitle>}
        {tagline && <HeroTagline>{tagline}</HeroTagline>}
        {pricingLabel}
        {ctaText && (
          <HeroCTAButton onClick={onCtaClick}>{ctaText}</HeroCTAButton>
        )}
      </HeroContent>

      {showNavArrows && (
        <>
          <HeroNavButton
            $left
            onClick={onPrev}
            aria-label={prevAriaLabel}
            title={prevAriaLabel}
          >
            {prevArrow}
          </HeroNavButton>
          <HeroNavButton
            onClick={onNext}
            aria-label={nextAriaLabel}
            title={nextAriaLabel}
          >
            {nextArrow}
          </HeroNavButton>
        </>
      )}

      {children}
    </HeroSection>
  );
};

export default CinematicHero;
